import React, { useEffect } from 'react';
import { useEffectEvent } from 'use-effect-event';
import useRouteHandle from '../useRouteHandle';
import PageLayout from './PageLayout';
import { useLogout } from '../../account/localLogout';
import { UserType } from '../../graphql/generated/graphql';
import useShowInvitationModal from '../../organization/useShowInvitationModal';
import NewTopicModal from '../../topic/modal/NewTopicModal';
import { UIActions } from '../../ui/uiSlice';
import { isElectron } from '../../utils/desktopClient';
import { createPostPath } from '../../utils/url/postPath';
import useAppSelector, { useAppDispatch } from '../redux/useAppSelector';

const batchOn = (...results: unknown[]) => {
  return () => {
    results
      .filter((result) => typeof result === 'function')
      .forEach((result) => result());
  };
};

const useExposingDesktopFeatures = isElectron
  ? () => {
      const user = useAppSelector((state) => state.session.user);
      const showInvitationModal = useEffectEvent(useShowInvitationModal());

      const isLoggedIn = !!user.id;
      const isUserOrAdmin =
        isLoggedIn &&
        (user.type === UserType.User || user.type === UserType.Admin);

      const dispatch = useAppDispatch();
      useEffect(() => {
        if (!isUserOrAdmin) return undefined;

        return batchOn(
          window.desktop?.onFeatureRequested?.('inviteTeammates', () => {
            showInvitationModal();
          }),
          window.desktop?.onFeatureRequested?.('createTopic', () => {
            dispatch(UIActions.openModal(NewTopicModal));
          }),
        );
      }, [isUserOrAdmin, dispatch]);

      const logout = useEffectEvent(useLogout());
      useEffect(() => {
        if (!isLoggedIn) return undefined;

        return batchOn(
          window.desktop?.onFeatureRequested?.('logout', () => {
            logout();
          }),
          window.desktop?.onFeatureRequested?.('createPost', () => {
            window.open(createPostPath(), '_blank', 'noopener');
          }),
          window.desktop?.onFeatureRequested?.('copyLinkToTab', () => {
            navigator.clipboard.writeText(window.location.href).then(() => {
              dispatch(
                UIActions.pushToast({
                  level: 'success',
                  message: 'Link copied!',
                }),
              );
            });
          }),
        );
      }, [isLoggedIn, dispatch]);
    }
  : () => {};

const InternalApp = () => {
  const { routeName, pageLayout = {} } = useRouteHandle();

  useExposingDesktopFeatures();

  return <PageLayout.Root elements={pageLayout} routeName={routeName} />;
};

export default InternalApp;
